import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Upgrade,
  Cancel,
  CheckCircle,
  Star,
  Payment,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ListLoadErrorAlert from '../../components/Common/ListLoadErrorAlert';
import { getAuthToken } from '../../lib/authToken';

const authHeaders = () => ({
  Authorization: `Bearer ${getAuthToken()}`,
  'Content-Type': 'application/json',
});

const SubscriptionPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  useEffect(() => {
    const checkout = searchParams.get('checkout');
    if (checkout === 'success') {
      toast.success('Checkout completed — refreshing subscription');
      queryClient.invalidateQueries({ queryKey: ['subscription-summary', user?.tenantId] });
      searchParams.delete('checkout');
      searchParams.delete('session_id');
      searchParams.delete('simulated');
      setSearchParams(searchParams, { replace: true });
    } else if (checkout === 'cancelled') {
      toast.error('Checkout cancelled');
      searchParams.delete('checkout');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const { data: summaryData, isLoading, error, refetch } = useQuery({
    queryKey: ['subscription-summary', user?.tenantId],
    queryFn: async () => {
      const res = await fetch('/api/subscriptions', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load subscription');
      return json.data;
    },
  });

  const { data: plansData } = useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const res = await fetch('/api/subscriptions/plans', { headers: authHeaders() });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load plans');
      return json.data as any[];
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: async () => {
      if (!selectedPlanId) throw new Error('Select a plan first');
      const res = await fetch('/api/subscriptions/checkout', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ planId: selectedPlanId, billingCycle }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to start checkout');
      return json.data as { url: string; simulated?: boolean; stripeConfigured?: boolean };
    },
    onSuccess: (data) => {
      setUpgradeOpen(false);
      if (data.simulated) {
        queryClient.invalidateQueries({ queryKey: ['subscription-summary', user?.tenantId] });
        toast.success('Dev checkout simulated — subscription upgraded');
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('No checkout URL returned');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const upgradeMutation = useMutation({
    mutationFn: async () => {
      const isDemoUpgrade = summaryData?.subscription?.type === 'demo'
        || summaryData?.subscription?.type === 'trial';
      const url = isDemoUpgrade
        ? '/api/subscriptions/upgrade'
        : `/api/subscriptions/${summaryData?.subscription?.id}/upgrade`;
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          planId: selectedPlanId,
          billingCycle,
          paymentData: { success: true },
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Upgrade failed');
      return json;
    },
    onSuccess: (json) => {
      queryClient.invalidateQueries({ queryKey: ['subscription-summary', user?.tenantId] });
      setUpgradeOpen(false);
      const conv = json.data?.conversion;
      toast.success(
        conv
          ? `Converted from ${conv.sourcePlan} to ${conv.targetPlan}`
          : 'Subscription upgraded successfully'
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const subId = summaryData?.subscription?.id;
      if (!subId) throw new Error('No subscription to cancel');
      const res = await fetch(`/api/subscriptions/${subId}/cancel`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ reason: cancelReason || 'Cancelled by admin' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Cancel failed');
      return json;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription-summary', user?.tenantId] });
      setCancelOpen(false);
      toast.success('Subscription cancelled');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    console.error('Subscription error:', error);
  }

  const sub = summaryData?.subscription;
  const plan = summaryData?.plan;
  const isDemo = sub?.type === 'demo' || sub?.type === 'trial';
  const paidPlans = (plansData || []).filter(
    (p: any) => p.type === 'paid' || p.slug === 'basic' || p.slug === 'professional' || p.slug === 'enterprise'
  );

  const priceFor = (p: any) => {
    if (typeof p.getPrice === 'function') return p.getPrice(billingCycle);
    if (billingCycle === 'yearly') return p.yearlyPrice ?? p.price * 10;
    if (billingCycle === 'quarterly') return p.quarterlyPrice ?? p.price * 3;
    return p.monthlyPrice ?? p.price ?? 0;
  };

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <ListLoadErrorAlert
          resourceLabel="subscription"
          message="Subscription details couldn’t be loaded right now. You can retry or try upgrading again shortly."
          onRetry={() => refetch()}
        />
      )}
      <Typography variant="h4" gutterBottom>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage your ArgisLegal plan, upgrade from demo, or cancel.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Current plan
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  label={(plan?.name || sub?.type || 'Unknown').toString()}
                  color={isDemo ? 'warning' : 'success'}
                  icon={isDemo ? undefined : <CheckCircle />}
                />
                <Chip label={sub?.status || 'unknown'} size="small" variant="outlined" />
                {sub?.billingCycle && <Chip label={sub.billingCycle} size="small" />}
              </Box>
              {sub?.amount != null && (
                <Typography variant="h5" sx={{ mb: 1 }}>
                  ${Number(sub.amount).toFixed(2)}
                  <Typography component="span" variant="body2" color="text.secondary">
                    /{sub.billingCycle || 'period'}
                  </Typography>
                </Typography>
              )}
              {sub?.endDate && (
                <Typography variant="body2" color="text.secondary">
                  {isDemo ? 'Demo ends' : 'Renews / ends'}:{' '}
                  {format(new Date(sub.endDate), 'MMM d, yyyy')}
                </Typography>
              )}
              {summaryData?.tenant?.convertedAt && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Converted {format(new Date(summaryData.tenant.convertedAt), 'MMM d, yyyy')}
                  {summaryData.tenant.conversionSourcePlan
                    ? ` from ${summaryData.tenant.conversionSourcePlan}`
                    : ''}
                </Alert>
              )}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
              {isDemo && (
                <Button
                  variant="contained"
                  startIcon={<Upgrade />}
                  onClick={() => setUpgradeOpen(true)}
                >
                  Upgrade to paid
                </Button>
              )}
              {!isDemo && sub?.status === 'active' && (
                <Button
                  color="error"
                  variant="outlined"
                  startIcon={<Cancel />}
                  onClick={() => setCancelOpen(true)}
                >
                  Cancel
                </Button>
              )}
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Typography variant="h6" gutterBottom>
            Available plans
          </Typography>
          <FormControl size="small" sx={{ mb: 2, minWidth: 160 }}>
            <InputLabel>Billing cycle</InputLabel>
            <Select
              label="Billing cycle"
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as any)}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            {paidPlans.map((p: any) => (
              <Grid item xs={12} sm={6} key={p.id}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderColor: selectedPlanId === p.id ? 'primary.main' : 'divider',
                    borderWidth: selectedPlanId === p.id ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="h6">{p.name}</Typography>
                      {p.slug === 'enterprise' && <Star fontSize="small" color="warning" />}
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1 }}>
                      ${Number(priceFor(p)).toFixed(0)}
                      <Typography component="span" variant="caption" color="text.secondary">
                        /{billingCycle === 'monthly' ? 'mo' : billingCycle}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {p.description || p.slug}
                    </Typography>
                    {p.features?.whiteLabel && (
                      <Chip size="small" label="White-label" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                    {p.features?.sso && (
                      <Chip size="small" label="SSO" sx={{ mr: 0.5, mb: 0.5 }} />
                    )}
                  </CardContent>
                  <CardActions>
                    <Button
                      size="small"
                      variant={selectedPlanId === p.id ? 'contained' : 'outlined'}
                      onClick={() => {
                        setSelectedPlanId(p.id);
                        setUpgradeOpen(true);
                      }}
                    >
                      {isDemo ? 'Select & upgrade' : 'Switch plan'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      <Dialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{isDemo ? 'Upgrade from demo' : 'Change plan'}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            {isDemo
              ? 'This converts your demo workspace to a paid plan and records a conversion event for growth analytics.'
              : 'Your plan will be updated for the next billing period.'}
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              label="Plan"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            >
              {paidPlans.map((p: any) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — ${Number(priceFor(p)).toFixed(0)}/{billingCycle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Billing cycle</InputLabel>
            <Select
              label="Billing cycle"
              value={billingCycle}
              onChange={(e) => setBillingCycle(e.target.value as any)}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpgradeOpen(false)}>Cancel</Button>
          <Button
            variant="outlined"
            disabled={!selectedPlanId || upgradeMutation.isPending || checkoutMutation.isPending}
            onClick={() => upgradeMutation.mutate()}
          >
            {upgradeMutation.isPending ? <CircularProgress size={22} /> : 'Upgrade without Stripe'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Payment />}
            disabled={!selectedPlanId || checkoutMutation.isPending}
            onClick={() => checkoutMutation.mutate()}
          >
            {checkoutMutation.isPending ? <CircularProgress size={22} /> : 'Checkout with Stripe'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel subscription</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cancelling will deactivate your paid subscription. You can re-subscribe later.
          </Alert>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>Keep plan</Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            {cancelMutation.isPending ? <CircularProgress size={22} /> : 'Confirm cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPage;
